import {
  getRecordCollection,
  queryRecordCollection,
} from "~/api/generated/record-service/business-object/business-object";
import type { CollectionQueryRequest } from "~/api/generated/record-service/model/collectionQueryRequest";
import type { FilterNode } from "~/api/generated/record-service/model/filterNode";
import type { GetRecordCollectionParams } from "~/api/generated/record-service/model/getRecordCollectionParams";
import { ApiError, getApiErrorMessage, hasApiErrorStatus } from "~/lib/apiClient";

import { parseRecordCollection, type RecordWithMeta } from "../api/recordParsers";
import { type CollectionFacet, parseFacets } from "./collectionFacets";
import {
  type CollectionAggregationSpec,
  type CollectionGroupTree,
  type CollectionQueryTotals,
  hasGroupQueryFields,
  isUnsupportedGroupingError,
  parseCollectionGroups,
  parseCollectionTotals,
  toCollectionQueryGroupFields,
  withoutGroupQueryFields,
} from "./collectionQueryGroups";
import {
  type ParentScopedChildQuery,
  postParentScopedChildrenQuery,
} from "./parentScopedChildQuery";

/** Params for record-service collection reads (GET table mode or POST `/query`). */
export interface CollectionQueryParams {
  limit?: number;
  offset?: number;
  /** Record-service `sortBy`, e.g. `asc(code),desc(name)`. */
  sortBy?: string;
  /** Flat or explicit AST filter — encoded on GET, sent in POST body. */
  filter?: FilterNode;
  /** Enum field names for POST `/query` facet buckets (counts in response). */
  facets?: string[];
  /** Full-text `q` is rejected by record-service when n-grams are off. Use `filter` + `contains`. */
  q?: string;
  /**
   * Named view applied server-side (`x-view` header). View-aware record
   * services project the response to the view's fields and join its edges;
   * older builds ignore the header. Omitted for `default` (BE convention).
   */
  viewName?: string;
  /** Phase 1 OpenSearch group fields — POST `/query` only; omit empty. */
  groupBy?: string[];
  /** Phase 1 metric specs — POST `/query` only; omit empty. */
  aggregations?: CollectionAggregationSpec[];
  /**
   * When set, the read posts to the parent-scoped children route instead of
   * the global collection. The body is still built by `toCollectionQueryRequest`
   * and must not contain `parent`. A 404 is not retried against the global query.
   */
  parentScope?: ParentScopedChildQuery;
}

/**
 * True when any table-mode param is set (triggers paginated envelope + server total).
 *
 * Typed as a predicate so callers narrow `params` to non-undefined on the true
 * branch instead of reaching for a non-null assertion.
 */
export const isCollectionTableMode = (
  params?: CollectionQueryParams,
): params is CollectionQueryParams =>
  params !== undefined &&
  (params.limit !== undefined ||
    params.offset !== undefined ||
    params.sortBy !== undefined ||
    params.filter !== undefined ||
    (params.facets?.length ?? 0) > 0 ||
    (params.q !== undefined && params.q.length > 0) ||
    (params.groupBy?.length ?? 0) > 0 ||
    (params.aggregations?.length ?? 0) > 0 ||
    params.parentScope !== undefined);

export const toGetRecordCollectionParams = (
  params: CollectionQueryParams,
): GetRecordCollectionParams => {
  const query: GetRecordCollectionParams = {};

  if (params.limit !== undefined) {
    query.limit = params.limit;
  }
  if (params.offset !== undefined) {
    query.offset = params.offset;
  }
  if (params.sortBy !== undefined) {
    query.sortBy = params.sortBy;
  }
  if (params.filter !== undefined) {
    query.filter = JSON.stringify(params.filter);
  }

  return query;
};

export const toCollectionQueryRequest = (params: CollectionQueryParams): CollectionQueryRequest => {
  const extras = toCollectionQueryGroupFields(params);
  return {
    limit: params.limit,
    offset: params.offset,
    sortBy: params.sortBy,
    filter: params.filter,
    facets: params.facets?.length ? params.facets : undefined,
    ...extras,
  };
};

/** Which record-service engine served the read (`meta.engine`). */
export type CollectionQueryEngine = "postgres" | "opensearch";

export interface ParsedCollectionQueryPage {
  records: RecordWithMeta[];
  total: number | null;
  facets: CollectionFacet[];
  /** Engine reported by `meta.engine`, when present. */
  engine?: CollectionQueryEngine;
  groups?: CollectionGroupTree;
  totals?: CollectionQueryTotals;
}

const readTotal = (raw: unknown): number | null => {
  if (raw && typeof raw === "object" && "total" in raw) {
    const total = (raw as { total?: unknown }).total;
    return typeof total === "number" ? total : null;
  }

  return null;
};

const readFacetsRaw = (raw: unknown): unknown => {
  if (raw && typeof raw === "object" && "facets" in raw) {
    return (raw as { facets?: unknown }).facets;
  }

  return undefined;
};

const readEngine = (raw: unknown): CollectionQueryEngine | undefined => {
  if (!raw || typeof raw !== "object" || !("meta" in raw)) {
    return undefined;
  }

  const meta = (raw as { meta?: unknown }).meta;
  if (!meta || typeof meta !== "object" || !("engine" in meta)) {
    return undefined;
  }

  const engine = (meta as { engine?: unknown }).engine;
  return engine === "postgres" || engine === "opensearch" ? engine : undefined;
};

/**
 * Coerce a collection-query response (GET table mode or POST `/query`) into
 * records, optional total, enum facet buckets, and the serving engine.
 */
export const parseCollectionQueryEnvelope = (raw: unknown): ParsedCollectionQueryPage => ({
  records: parseRecordCollection(raw),
  total: readTotal(raw),
  facets: parseFacets(readFacetsRaw(raw)),
  engine: readEngine(raw),
  groups: parseCollectionGroups(raw),
  totals: parseCollectionTotals(raw),
});

/** Table loads use POST `/query` (CPES-13160); GET is the compatibility fallback. */
export const shouldPostCollectionQuery = (params: CollectionQueryParams): boolean =>
  isCollectionTableMode(params);

const isPostQueryUnavailable = (error: unknown): boolean =>
  hasApiErrorStatus(error, 501) || hasApiErrorStatus(error, 404);

/** `x-view` header for view-scoped reads; `undefined` for the default view. */
const viewHeaders = (params: CollectionQueryParams): Record<string, string> | undefined =>
  params.viewName && params.viewName !== "default" ? { "x-view": params.viewName } : undefined;

export interface FetchCollectionQueryArgs {
  resource: string;
  version: string;
  params: CollectionQueryParams;
  signal?: AbortSignal;
}

/**
 * `customFetch` deliberately does NOT throw on a non-2xx GET — relationship
 * reads use `{ status: 404 }` as an ordinary "not found" result (see the
 * `throwOnError` comment in `app/api/custom-fetch.ts`). A collection read has
 * no such convention: a 404 error body `{ status, message }` is structurally
 * indistinguishable from any other shape `parseRecordCollection` does not
 * recognise. Reject here, before the body ever reaches the parser, so the
 * caller's query errors instead of silently resolving an empty page.
 */
const assertCollectionResponseOk = (response: { data: unknown; status: number }): void => {
  if (response.status >= 200 && response.status < 300) {
    return;
  }

  const body = response.data;
  const hasStringMessage =
    body !== null &&
    typeof body === "object" &&
    "message" in body &&
    typeof (body as { message?: unknown }).message === "string";

  throw new ApiError({
    message: hasStringMessage ? (body as { message: string }).message : `HTTP ${response.status}`,
    status: response.status,
    body,
  });
};

const fetchCollectionViaGet = async (
  resource: string,
  version: string,
  params: CollectionQueryParams,
  signal?: AbortSignal,
): Promise<ParsedCollectionQueryPage> => {
  const response = await getRecordCollection(
    resource,
    version,
    toGetRecordCollectionParams(params),
    {
      ...(signal ? { signal } : {}),
      ...(viewHeaders(params) ? { headers: viewHeaders(params) } : {}),
    },
  );
  assertCollectionResponseOk(response);
  return parseCollectionQueryEnvelope(response.data);
};

/**
 * Executes a table-mode collection read via POST `/query`, falling back to GET
 * table mode when `/query` is not deployed (501/404).
 *
 * `parentScope` posts the parent-path children query instead, and does not
 * fall back to the global collection.
 */
export const fetchCollectionQuery = async ({
  resource,
  version,
  params,
  signal,
}: FetchCollectionQueryArgs): Promise<ParsedCollectionQueryPage> => {
  if (params.parentScope) {
    const raw = await postParentScopedChildrenQuery(
      params.parentScope,
      toCollectionQueryRequest(params),
      viewHeaders(params),
      signal,
    );
    return parseCollectionQueryEnvelope(raw);
  }

  const requestOptions: RequestInit = {
    ...(signal ? { signal } : {}),
    ...(viewHeaders(params) ? { headers: viewHeaders(params) } : {}),
  };

  if (!shouldPostCollectionQuery(params)) {
    return fetchCollectionViaGet(resource, version, params, signal);
  }

  try {
    const response = await queryRecordCollection(
      resource,
      version,
      toCollectionQueryRequest(params),
      requestOptions,
    );
    return parseCollectionQueryEnvelope(response.data);
  } catch (error) {
    if (hasGroupQueryFields(params) && isUnsupportedGroupingError(error)) {
      const response = await queryRecordCollection(
        resource,
        version,
        toCollectionQueryRequest(withoutGroupQueryFields(params)),
        requestOptions,
      );
      return parseCollectionQueryEnvelope(response.data);
    }

    if (!isPostQueryUnavailable(error)) {
      throw error;
    }

    // Compatibility: older record-service builds expose GET table mode only.
    const {
      facets: _facets,
      groupBy: _groupBy,
      aggregations: _aggregations,
      ...getParams
    } = params;
    return fetchCollectionViaGet(resource, version, getParams, signal);
  }
};

/**
 * Postgres supports `eq`, `in`, `and`, `contains`, and compound `or`.
 * OpenSearch adds negation, remaining affix, range, and `exists` operators.
 * Detect the hard 400s so type-ahead can fall back instead of showing an empty list.
 */
export const isUnsupportedFilterOpError = (error: unknown): boolean => {
  if (!hasApiErrorStatus(error, 400)) {
    return false;
  }
  const message = getApiErrorMessage(error, "");
  return (
    /Filter op ['"]?\w+['"]? is not supported by engine/i.test(message) ||
    /Unknown filter field:\s*(or|and)\b/i.test(message) ||
    /not supported by engine postgres/i.test(message)
  );
};
